const LEAD_MEDIA_TYPES = ['post_pictures', 'post_artworks', 'post_videos'];

const removeColorStylesRecursive = node => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    if (node.hasAttribute('style')) {
      const styles = node
        .getAttribute('style')
        .split(';')
        .map(part => part.trim())
        .filter(part => {
          const property = part.toLowerCase();
          return property && !property.startsWith('color:')
            && !property.startsWith('font-family:');
        });

      if (styles.length) node.setAttribute('style', styles.join('; '));
      else node.removeAttribute('style');
    }

    node.removeAttribute('color');
  }

  Array.from(node.childNodes || []).forEach(removeColorStylesRecursive);
};

export const sanitizePostHtml = html => {
  if (!html || typeof document === 'undefined') return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('style').forEach(style => style.remove());
  template.content.querySelectorAll('font').forEach(font => {
    font.replaceWith(...font.childNodes);
  });
  Array.from(template.content.childNodes).forEach(removeColorStylesRecursive);

  return template.innerHTML;
};

const hasMeaningfulTextContent = content => (
  String(content || '')
    .replace(/<[^>]*>/g, '')
    .replace(/[\u00A0\u200B]/g, '')
    .trim()
    .length > 0
);

export const hasMeaningfulPostText = texts => (
  (texts || []).some(text => hasMeaningfulTextContent(text.content))
);

const getPostLeadMediaStructure = formState => [
  ...(formState.post_pictures || []).flatMap(group => {
    const id = Number(group?.groupId);
    return Number.isInteger(id) ? [{ type: 'post_pictures', id }] : [];
  }),
  ...(formState.post_artworks || []).flatMap(group => {
    const id = Number(group?.groupId);
    return Number.isInteger(id) ? [{ type: 'post_artworks', id }] : [];
  }),
  ...(formState.post_videos || []).flatMap(group => {
    const id = Number(group?.groupId);
    return Number.isInteger(id) ? [{ type: 'post_videos', id }] : [];
  }),
];

const serializePostVideos = videos => (videos || []).map(video => (
  video.assetId
    ? {
      groupId: video.groupId,
      assetId: video.assetId,
      caption: video.caption || '',
    }
    : {
      videoId: video.videoId,
      url: video.url || video.playbackUrl,
      caption: video.caption || '',
    }
));

// The editor keeps hydrated user records so it can render selected tags. The
// post API persists the User primary keys, not those presentation records.
const serializePostTags = tags => (tags || []).map(tag => (
  typeof tag === 'string' ? tag : tag._id
));

/**
 * Article documents are already ordered state. Saving must not inspect the
 * contentEditable DOM, which is only an input surface for text blocks.
 */
export const getArticlePayload = formState => ({
  ...formState,
  post_tags: serializePostTags(formState.post_tags),
  post_videos: serializePostVideos(formState.post_videos),
});

export const getPostPayload = formState => {
  const postTexts = (formState.post_texts || []).filter(text => (
    hasMeaningfulTextContent(text.content)
  ));
  const textIds = new Set(postTexts.map(text => String(text.textId)));

  return {
    ...formState,
    post_tags: serializePostTags(formState.post_tags),
    post_videos: serializePostVideos(formState.post_videos),
    post_texts: postTexts,
    post_structure: [
      ...getPostLeadMediaStructure(formState),
      ...(formState.post_structure || []).filter(block => (
        !LEAD_MEDIA_TYPES.includes(block.type)
        && (block.type !== 'post_texts' || textIds.has(String(block.id)))
      )),
    ],
  };
};

export const hasDraftablePostContent = formState => {
  const payload = getPostPayload(formState);

  return hasMeaningfulPostText(payload.post_texts)
    || (payload.post_pictures || []).length > 0
    || (payload.post_artworks || []).length > 0
    || (payload.post_videos || []).length > 0
    || (payload.post_polls || []).length > 0
    || (payload.post_quizzes || []).length > 0;
};
